import { shallowEqual } from "react-redux";
import { type CarConfigDispatchType, useCarConfigSelector } from "../../redux/store";
import { selectMotorization } from "../../redux/slices/CarConfigMotorizationSlice";
import type { AtmosphereWheeldriveType } from "../../utils/types/AtmosphereType";
import { find } from "../../../../../common-deprecated/utils";
import {
    CarConfigStep,
    getCarConfigStepPath,
} from "../../../../../common-deprecated/features/apheleia/utils/carConfigNavigationUtils";
import { changeCarId } from "../../utils/navigationUtils";
import { selectCarMotorization, setMotorization } from "../../redux/slices/CarConfigCarSlice";
import { trackPowertrainAddToBasket } from "../../utils/tracking";
import { type ParamModificationType, modifyQueryParams } from "../../utils/basketUtils";
import type { MotorizationType } from "../../../../shared-logic/types/Motorization";
import { setNavigationIds } from "../../redux/slices/CarConfigNavigationSlice";
import { useDispatch } from "../../../../../shared-logic/redux/store";

/*
 * Custom "Powertrains" logic for the LBX configure.
 * Gets the motorization info for a certain wheeldrive + selection logic based on packs
 */
const useAphLbxPowertrain = (
    wheeldrive: AtmosphereWheeldriveType,
): { selectWheeldrive: () => void; notAvailableInCurrentPacks: boolean; motorization?: MotorizationType } | null => {
    const dispatch = useDispatch<CarConfigDispatchType>();
    const motorizations = useCarConfigSelector(selectMotorization, shallowEqual);
    const car = useCarConfigSelector((state) => state.car);
    const basket = useCarConfigSelector((state) => state.basket);
    const currentSelectedPacks = basket.packs;
    const carConfigQueries = useCarConfigSelector((state) => state.carConfigNavigation.basketParams);
    const modelId = useCarConfigSelector((state) => state.car.model.id);
    const currentMotorization = useCarConfigSelector(selectCarMotorization);
    const packs = useCarConfigSelector((state) => state.items.packs);
    const standardPacksSelectedWheeldrive = wheeldrive.packs.filter((pack) => pack.standard);

    // Get the motorization based on the wheeldrive
    const motorization = motorizations.find((moto) => moto.wheeldrive.id === wheeldrive.id);
    if (!motorization) return null;

    const isActive =
        currentMotorization.engine.id === motorization?.engine.id &&
        currentMotorization.wheeldrive.id === motorization?.wheeldrive.id &&
        currentMotorization.transmission.id === motorization?.transmission.id;

    // Show the currently available wheeldrives by crossreferencing their packs to the ones selected in the basket.
    // When there are none selected, make sure to set the first one that matches the current motorization to be available and thus selected.
    const notAvailableInCurrentPacks = !(
        !wheeldrive.packs.some((pack) => pack.standard) ||
        isActive ||
        currentSelectedPacks.some((pack) =>
            wheeldrive.packs.filter((pck) => pck.standard).some((pck) => pck.id === pack.id),
        )
    );

    const selectWheeldrive = (): void => {
        trackPowertrainAddToBasket();
        // Update the URL with the new CarID due to the selected motorization
        // Also update this in redux as this does not happen automatically

        dispatch(setNavigationIds({ modelId, carId: motorization.carId }));
        changeCarId(CarConfigStep.Configure, modelId, motorization.carId);

        const path = getCarConfigStepPath(CarConfigStep.Configure, modelId, motorization.carId);

        // We first need to know if the previously selected wheeldrive has standard packs
        const previousWheeldriveStandardPacksShortIds: string[] = [];
        if (packs.filter((pack) => pack.standard).length !== 0) {
            previousWheeldriveStandardPacksShortIds.push(
                ...packs
                    .filter((pack) => pack.standard && !find(pack.shortId, currentSelectedPacks))
                    .map((pack) => pack.shortId),
            );
        }

        // Filters current selected packs for packs with included packs.
        // Checks those included ones and add MISSING packs from the basket.
        const includedPacksToAdd: string[] = [];
        const packsInBasketWithIncluded = currentSelectedPacks.filter((pack) => pack.includePacks.length > 0);
        packsInBasketWithIncluded.forEach((packInBasketWithIncluded) => {
            // We can't add just add all included packs as they might have a parent. This would result in duplicate and incorrect values.
            const hasParentPack = packsInBasketWithIncluded.find((packTemp) =>
                packTemp.includePacks.includes(packInBasketWithIncluded.id),
            );
            // These packs also can't be standard, as these should not be added to the basket.
            const isStandardPack = standardPacksSelectedWheeldrive.find(
                (standardPack) => standardPack.id === packInBasketWithIncluded.id,
            );

            if (!hasParentPack && !isStandardPack) includedPacksToAdd.push(packInBasketWithIncluded.shortId);
        });

        const packsToSet = Array.from(new Set([...includedPacksToAdd, ...previousWheeldriveStandardPacksShortIds]));

        // Remove all extras from the basket
        const emptyExtrasParam: ParamModificationType = { action: "set", param: "e", shortIds: [] };

        if (packsToSet.length > 0) {
            modifyQueryParams(carConfigQueries, dispatch, basket, car, path, [
                {
                    param: "p",
                    shortIds: packsToSet,
                    action: "set",
                },
                emptyExtrasParam,
            ]);
        } else {
            // Remove packs that have become standard in the new wheeldrive from the query and basket.
            const standardShortIds = packs
                .filter((pack) => find(pack.id, standardPacksSelectedWheeldrive))
                .map((pack) => pack.shortId);

            modifyQueryParams(carConfigQueries, dispatch, basket, car, path, [
                {
                    param: "p",
                    shortIds: standardShortIds,
                    action: "filter",
                },
                emptyExtrasParam,
            ]);
        }

        dispatch(
            setMotorization({
                engine: {
                    id: motorization.engine.id,
                    name: motorization.engine.name,
                    hybrid: motorization.engine.type.hybrid,
                    electric: motorization.engine.type.electric,
                    fuelCell: motorization.engine.type.fuelCell,
                    plugin: motorization.engine.type.plugin,
                    internalCode: motorization.engine.internalCode,
                    type: motorization.engine.type.name,
                    typeId: motorization.engine.type.id,
                    typeCode: motorization.engine.type.internalCode,
                },
                transmission: {
                    id: motorization.transmission.id,
                    internalCode: motorization.transmission.internalCode,
                    name: motorization.transmission.name,
                },
                wheeldrive: {
                    id: motorization.wheeldrive.id,
                    internalCode: motorization.wheeldrive.internalCode,
                    name: motorization.wheeldrive.name,
                    description: motorization.wheeldrive.description,
                },
            }),
        );
    };

    return {
        motorization,
        notAvailableInCurrentPacks,
        selectWheeldrive,
    };
};

export default useAphLbxPowertrain;
